import { useState, useRef, useEffect, useCallback } from 'react'

// For React development, workers need to be in the public folder
// Move your worker files to: public/workers/
const workerMap = {
    formatList: '/workers/formatListWorker.js',
    filterFormatList: '/workers/filterFormatListWorker.js',
}

export const useWebWorker = (workerFunctionName) => {
    const [data, setData] = useState(null)
    const [error, setError] = useState(null)
    const [loading, setLoading] = useState(false)
    const workerRef = useRef(null)

    useEffect(() => {
        // Clean up worker on unmount
        return () => {
            if (workerRef.current) {
                workerRef.current.terminate()
                workerRef.current = null
            }
        }
    }, [])

    const runWorker = useCallback(
        (input, timeout = 30000) => {
            const workerPath = workerMap[workerFunctionName]

            if (!workerPath) {
                console.error(
                    `No worker path found for function: ${workerFunctionName}`,
                )
                setError(
                    new Error(`Worker not found for: ${workerFunctionName}`),
                )
                return
            }

            // Terminate existing worker if it exists
            if (workerRef.current) {
                workerRef.current.terminate()
            }

            try {
                // Create a new Worker
                workerRef.current = new Worker(workerPath)
                setLoading(true)
                setError(null)

                // Set up event listeners
                workerRef.current.onmessage = (e) => {
                    if (e.data.success) {
                        setData(e.data.data)
                    } else {
                        setError(new Error(e.data.error.message))
                        console.error('Worker error:', e.data.error)
                    }
                    setLoading(false)
                }

                workerRef.current.onerror = (err) => {
                    console.error('Worker error:', err)
                    setError(err)
                    setLoading(false)
                }

                // Set timeout if specified
                let timeoutId = null
                if (timeout > 0) {
                    timeoutId = setTimeout(() => {
                        setError(
                            new Error(`Operation timed out after ${timeout}ms`),
                        )
                        setLoading(false)

                        if (workerRef.current) {
                            workerRef.current.terminate()
                            workerRef.current = null
                        }
                    }, timeout)
                }

                // Post message to worker
                workerRef.current.postMessage(input)

                // Return cleanup function
                return () => {
                    if (timeoutId) {
                        clearTimeout(timeoutId)
                    }
                }
            } catch (err) {
                console.error('Error creating worker:', err)
                setError(err)
                setLoading(false)
            }
        },
        [workerFunctionName],
    )

    const cancelWorker = useCallback(() => {
        if (loading && workerRef.current) {
            workerRef.current.terminate()
            workerRef.current = null
            setLoading(false)
            setError(new Error('Operation canceled'))
        }
    }, [loading])

    return { data, error, loading, runWorker, cancelWorker }
}
